import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useAuth, authenticateUser, authmeUser, authenticateSSO, getAddInOrigin, UnicliTheme, LoginForm, WelcomeScreen } from "common";
import UnicliChat from "./UnicliChat";
import { loadTheme } from "@fluentui/react";
import { jwtDecode } from "jwt-decode";
import { describeMailboxItem } from "../utils/outlookLoadDebug";
import { acquireGraphAPIToken, getOfficeAccessToken, getOfficeGraphAccessToken, isGraphTokenExpired, isIdentityApiUnsupported, resolveGraphTokenFromSso } from "../utils/graphToken";
import { getItemContextKey } from "../utils/itemContextKey";
import { isVstoHost } from "../../host";

loadTheme(UnicliTheme);

const App = ({ host }) => {
  const [authInProgress, setAuthInProgress] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [currentItem, setCurrentItem] = useState(() => host.getSnapshot?.() ?? null);
  const [isReady, setIsReady] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [user, setUser] = useState(null);
  const [graphAPIToken, setGraphAPIToken] = useState(null);

  const { authState, login, logout } = useAuth();
  const authStateRef = useRef(authState);
  const graphAPITokenRef = useRef(graphAPIToken);
  const loginRef = useRef(login);
  const vstoMode = isVstoHost(host);

  useEffect(() => {
    authStateRef.current = authState;
  }, [authState]);

  useEffect(() => {
    graphAPITokenRef.current = graphAPIToken;
  }, [graphAPIToken]);

  useEffect(() => {
    loginRef.current = login;
  }, [login]);

  useEffect(() => {
    const itemChangedHandler = (snapshot) => {
      console.log("[App] ItemChanged", snapshot);
      setCurrentItem((prev) => {
        const prevKey = getItemContextKey(prev);
        const nextKey = getItemContextKey(snapshot);
        // Same key: ignore (e.g. VSTO explorer when a reply compose opens for this message).
        // Empty keys must still update so emails without internetMessageId are not stuck.
        if (prevKey && nextKey && prevKey === nextKey) {
          return prev;
        }
        return snapshot;
      });

      if (vstoMode) {
        return;
      }

      const state = authStateRef.current;
      if (state?.authToken) {
        acquireGraphAPIToken({
          existingToken: graphAPITokenRef.current || state.graphAPIToken,
          authToken: state.authToken,
          user: state.user,
          login: (...args) => loginRef.current(...args),
        }).then((token) => {
          if (token) {
            setGraphAPIToken(token);
          }
        });
      }
    };

    const unsubscribe = host.onItemChanged(itemChangedHandler);

    host
      .waitForItemReady()
      .then((snapshot) => {
        console.log("[App] Initial item ready", vstoMode ? snapshot : describeMailboxItem(host.getOfficeItem?.()));
        setCurrentItem(snapshot);
        setIsReady(true);
      })
      .catch((error) => {
        console.error(error);
        setIsReady(true);
      });

    fetch("/version.json")
      .then((response) => response.json())
      .then((data) => console.log(`Add-in Version: ${data.version}`))
      .catch((error) => console.error("Error fetching version:", error));

    return () => {
      // Only drop this component's listener. Do not host.dispose() — that removes the
      // Office.js ItemChanged handler registered in onReady and is never re-attached.
      unsubscribe?.();
    };
  }, [host, vstoMode]);

  useEffect(() => {
    const initAuthFlow = async () => {
      setAuthInProgress(true);

      const storedFirstTimeViewed = localStorage.getItem("storedFirstTimeViewed");
      setIsFirstTime(!storedFirstTimeViewed);

      let didLogin = false;
      const storedAuth = localStorage.getItem("authState");
      if (storedAuth) {
        try {
          const { authToken, graphAPIToken: storedGraphToken } = JSON.parse(storedAuth);
          const response = await authmeUser(authToken);
          if (response) {
            setUser(response);

            if (!vstoMode) {
              if (storedGraphToken && !isGraphTokenExpired(storedGraphToken)) {
                setGraphAPIToken(storedGraphToken);
                console.log("Graph API token retrieved from storage");
              } else if (!isIdentityApiUnsupported()) {
                console.warn("Graph API token missing or expired, attempting backfill via SSO");
                const backfilled = await acquireGraphAPIToken({
                  existingToken: storedGraphToken,
                  authToken,
                  user: response,
                  login,
                });
                if (backfilled) {
                  setGraphAPIToken(backfilled);
                }
              } else {
                console.warn("Graph API token missing and Office Identity API unavailable — Graph fallback disabled");
              }
            }

            didLogin = true;
          } else {
            logout();
          }
        } catch (error) {
          console.error("Token validation failed:", error);
          logout();
        }
      }

      if (!didLogin && !vstoMode && !isIdentityApiUnsupported()) {
        try {
          const officeToken = await getOfficeAccessToken();
          if (!officeToken) {
            console.warn("SSO skipped: Office access token unavailable (see [graphToken] logs above)");
          } else {
            const officeTokenDecoded = jwtDecode(officeToken);
            console.log("officeTokenDecoded", officeTokenDecoded);

            const { authToken, user, response } = await authenticateSSO(
              officeTokenDecoded.preferred_username,
              officeToken,
              getAddInOrigin()
            );
            if (authToken && user) {
              console.log("SSO response:", response);

              let graphToken = await resolveGraphTokenFromSso(response, officeToken);
              if (!graphToken) {
                graphToken = await getOfficeGraphAccessToken();
              }
              login(authToken, user, graphToken);
              if (graphToken) {
                setGraphAPIToken(graphToken);
                console.log("Graph API token stored successfully");
              } else {
                console.warn("No Graph API token found after SSO");
              }
              setUser(user);
              didLogin = true;
            }
          }
        } catch (error) {
          console.error("SSO login failed:", error);
        }
      }

      setAuthInProgress(false);
    };

    initAuthFlow();
  }, [login, logout, vstoMode]);

  const handleLogin = async (email, password) => {
    try {
      const { authToken, user: loggedInUser } = await authenticateUser(email, password);
      login(authToken, loggedInUser, null);
      setUser(loggedInUser);
      if (!vstoMode) {
        const graphToken = await acquireGraphAPIToken({ authToken, user: loggedInUser, login });
        if (graphToken) {
          setGraphAPIToken(graphToken);
        }
      }
    } catch (error) {
      console.error("Login failed:", error);
      setLoginError(true);
    }
  };

  const handleWelcomeScreenClick = () => {
    setIsFirstTime(false);
    localStorage.setItem("storedFirstTimeViewed", true);
  };

  if (authInProgress) {
    return <div>Authenticating...</div>;
  }

  if (!authState) {
    return (
      <>
        {isFirstTime ? (
          <WelcomeScreen onGettingStartedClick={handleWelcomeScreenClick} addinType="OUTLOOK" />
        ) : (
          <LoginForm onLogin={handleLogin} loginError={loginError} />
        )}
      </>
    );
  }

  if (!isReady) {
    return <div>Loading...</div>;
  }

  return (
    <UnicliChat
      host={host}
      currentItem={currentItem}
      currentUser={user}
      graphAPIToken={graphAPIToken}
      onGraphTokenAcquired={setGraphAPIToken}
    />
  );
};

App.propTypes = {
  title: PropTypes.string,
  host: PropTypes.object.isRequired,
};

export default App;
